import * as THREE from 'three';

/**
 * Calculate all projector statistics from node data
 * @param {Object} node - The projector node from findNodeById
 * @param {Object} projectorData - Optional projector library data
 * @returns {Object} All calculated projector statistics
 */
export function calculateProjectorStats(node, projectorData = null, userData) {
  if (!node || node.type !== 'projector') {
    return null;
  }

  const nodeData = node.data || {};

  // Get position and target from node data
  const position = new THREE.Vector3(nodeData.position?.x || 0, nodeData.position?.y || 0, nodeData.position?.z || 0);
  const targetPosition = new THREE.Vector3(nodeData.target?.x || 0, nodeData.target?.y || 0, nodeData.target?.z || 0);

  // Calculate distance
  //const distance = position.distanceTo(targetPosition)
  const distance = nodeData.throwDistance ?? 1;

  // Get projector properties (prefer library data for specs, node data for settings)
  const throwRatio = parseFloat(projectorData?.throw_ratio || nodeData.throwRatio || 1.4);
  const resolutionWidth = projectorData?.resolution_width || nodeData.resolutionWidth || 3840;
  const resolutionHeight = projectorData?.resolution_height || nodeData.resolutionHeight || 2160;
  const lumens = projectorData?.lumens || nodeData.lumens || 20000;
  const brightnessPercent = nodeData.power_brightness || 100;

  // Calculate projected dimensions
  const projectionWidth = distance / throwRatio;
  const aspectRatio = resolutionWidth / resolutionHeight;
  const projectionHeight = projectionWidth / aspectRatio;

  // Calculate pixel size (mm per pixel)
  const pixelSize = (projectionHeight * 1000) / resolutionHeight;

  // Calculate lux at center point
  const effectiveLumens = lumens * (brightnessPercent / 100);

  // Calculate spotlight angle from throw ratio
  // For a given distance, the image width determines the angle
  const actualImageWidth = distance / throwRatio;
  const halfAngle = Math.atan(actualImageWidth / 2 / distance);
  const angle = halfAngle * 2;

  // Calculate luminous intensity (candelas) for a spotlight
  // Formula: I = Φ / (2π × (1 - cos(θ/2)))
  const solidAngle = 2 * Math.PI * (1 - Math.cos(halfAngle));
  const luminousIntensity = effectiveLumens / solidAngle;

  // Calculate illuminance (lux) at center point
  // Formula: E = I / d²
  const luxAtCenter = luminousIntensity / (distance * distance);

  return {
    distance,
    pixelSize,
    throwRatio,
    resolution: `${resolutionWidth}x${resolutionHeight}`,
    resolutionWidth,
    resolutionHeight,
    projectionWidth,
    projectionHeight,
    lumens,
    brightnessPercent,
    angle, // in radians
    spotlightAngle: ((angle * 180) / Math.PI).toFixed(1), // in degrees
    luxAtCenter,
    position,
    targetPosition,
  };
}
